import { SORT_ORDER } from "@entities/brief/lib/constants";
import { InfluencerColumnStatusFilterValue } from "@widgets/CampaignBrief/lib/influencerColumnStatusFilter";
import { create } from "zustand";
import { devtools } from "zustand/middleware";

interface IFilter {
  dynamicFields: {
    [key: string]: {
      equalTo?: string[];
      range?: {
        from: number;
        to: number;
      };
    };
  };
  statusFilters: {
    isPendingQuote: boolean;
    isPendingDeliverables: boolean;
    isOfferReceived: boolean;
    isNegotiating: boolean;
    isDeclined: boolean;
    isInfluencerCommercialPending: boolean;
    isInfluencerCommercialNegoRequested: boolean;
    isInfluencerCommercialReceived: boolean;
    isInfluencerCommercialNegoReceived: boolean;
    isCreatorOnboardingPending?: boolean;
  };
  columnStatusFilters: InfluencerColumnStatusFilterValue[];
  delivrables: string[];
  socialMediaPlatforms?: string[];

  primaryContact?: {
    type: string[]; // ["Agency", "Manager","Self"]
    agencyId?: string[];
  };
  assignedPricingPocV2?: string[];

  createdAt?: {
    from?: string;
    to?: string;
  };
  updatedAt?: {
    from?: string;
    to?: string;
  };
  influencerCostDate?: {
    from?: string;
    to?: string;
  };
  sortByCreatedAt?: SORT_ORDER;
  sortByUpdatedAt?: SORT_ORDER;

  sortBy?: {
    followers?: SORT_ORDER;
    brandCost?: SORT_ORDER;
    sortByBriefFitEvalutionScore?: SORT_ORDER;
    sortByDynamicFields?: {
      [key: string]: SORT_ORDER;
    };
  };
  showDeclinedCreators: boolean;
}

interface useBriefInfluencerFilterState {
  feFilters: IFilter;
  beFilters: IFilter;
}

const getInitialState = (): useBriefInfluencerFilterState => ({
  feFilters: {
    dynamicFields: {},
    statusFilters: {
      isPendingQuote: false,
      isPendingDeliverables: false,
      isOfferReceived: false,
      isNegotiating: false,
      isDeclined: false,
      isInfluencerCommercialPending: false,
      isInfluencerCommercialNegoRequested: false,
      isInfluencerCommercialReceived: false,
      isCreatorOnboardingPending: false,
      isInfluencerCommercialNegoReceived: false,
    },
    columnStatusFilters: [],
    delivrables: [],
    primaryContact: {
      type: [],
      agencyId: [],
    },

    sortBy: undefined,
    showDeclinedCreators: false,
    socialMediaPlatforms: [],
  },
  beFilters: {
    dynamicFields: {},
    statusFilters: {
      isPendingQuote: false,
      isPendingDeliverables: false,
      isOfferReceived: false,
      isNegotiating: false,
      isDeclined: false,
      isInfluencerCommercialPending: false,
      isInfluencerCommercialNegoRequested: false,
      isInfluencerCommercialReceived: false,
      isCreatorOnboardingPending: false,
      isInfluencerCommercialNegoReceived: false,
    },
    columnStatusFilters: [],
    socialMediaPlatforms: [],
    delivrables: [],
    primaryContact: {
      type: [],
      agencyId: [],
    },

    sortBy: undefined,
    showDeclinedCreators: false,
  },
});

interface useBriefInfluencerFilterActions {
  setFilters: (filters: IFilter) => void;
  saveFilters: () => void;
  resetFilters: () => void;
  setDynamicField: (
    field: string,
    value?: string[] | { from: number; to: number }
  ) => void;

  setStatusFilter: (
    filter: keyof IFilter["statusFilters"],
    value: boolean
  ) => void;
  setDeliverables: (deliverables: string[]) => void;

  setSortBy: (sortBy: {
    followers?: SORT_ORDER;
    brandCost?: SORT_ORDER;
    sortByBriefFitEvalutionScore?: SORT_ORDER;
    sortByDynamicFields?: {
      [key: string]: SORT_ORDER;
    };
  }) => void;
  setCreatedAtFilter: (range?: { from?: string; to?: string }) => void;
  setUpdatedAtFilter: (range?: { from?: string; to?: string }) => void;
  setDateColumnSort: (sort: {
    sortByCreatedAt?: SORT_ORDER;
    sortByUpdatedAt?: SORT_ORDER;
  }) => void;
  removeSort: () => void;
  setShowDeclinedCreators: (showDeclinedCreators: boolean) => void;
}

export const useBriefInfluencerFilterStore = create<
  useBriefInfluencerFilterState & useBriefInfluencerFilterActions
>()(
  devtools((set, get) => ({
    ...getInitialState(),
    setFilters: (filters: IFilter) => {
      set({ feFilters: filters });
    },
    saveFilters: () => {
      set({ beFilters: get().feFilters });
    },
    resetFilters: () => {
      const initial = getInitialState();
      set({
        feFilters: initial.feFilters,
        beFilters: initial.beFilters,
      });
    },
    setDynamicField: (
      field: string,
      value?: string[] | { from: number; to: number }
    ) => {
      const { feFilters } = get();

      if (value) {
        const existingField = feFilters.dynamicFields[field];
        const isArray = Array.isArray(value);
        const isRange = !isArray && "from" in value && "to" in value;

        set((state) => ({
          feFilters: {
            ...state.feFilters,
            dynamicFields: {
              ...state.feFilters.dynamicFields,
              [field]: {
                equalTo: isArray ? value : existingField?.equalTo || undefined,
                range: isRange ? value : existingField?.range || undefined,
              },
            },
          },
        }));
      } else {
        const newDynamicFields = { ...feFilters.dynamicFields };
        delete newDynamicFields[field];
        set((state) => ({
          feFilters: {
            ...state.feFilters,
            dynamicFields: newDynamicFields,
          },
        }));
      }
    },
    setStatusFilter: (
      filter: keyof IFilter["statusFilters"],
      value: boolean
    ) => {
      set((state) => ({
        feFilters: {
          ...state.feFilters,
          statusFilters: { ...state.feFilters.statusFilters, [filter]: value },
        },
      }));
    },
    setDeliverables: (deliverables: string[]) => {
      set((state) => ({
        feFilters: { ...state.feFilters, delivrables: deliverables },
      }));
    },
    setIsDeclined: (isDeclined: boolean) => {
      set((state) => ({ feFilters: { ...state.feFilters, isDeclined } }));
    },
    reset: () => {
      set({ ...getInitialState() });
    },

    setSortBy: (sortBy: {
      followers?: SORT_ORDER;
      brandCost?: SORT_ORDER;
      sortByBriefFitEvalutionScore?: SORT_ORDER;
      sortByDynamicFields?: {
        [key: string]: SORT_ORDER;
      };
    }) => {
      set((state) => ({
        feFilters: {
          ...state.feFilters,
          sortBy,
          sortByCreatedAt: undefined,
          sortByUpdatedAt: undefined,
        },
      }));
    },
    setCreatedAtFilter: (range?: { from?: string; to?: string }) => {
      set((state) => ({
        feFilters: { ...state.feFilters, createdAt: range },
      }));
    },
    setUpdatedAtFilter: (range?: { from?: string; to?: string }) => {
      set((state) => ({
        feFilters: { ...state.feFilters, updatedAt: range },
      }));
    },
    setDateColumnSort: (sort: {
      sortByCreatedAt?: SORT_ORDER;
      sortByUpdatedAt?: SORT_ORDER;
    }) => {
      set((state) => ({
        feFilters: {
          ...state.feFilters,
          sortByCreatedAt: sort.sortByCreatedAt,
          sortByUpdatedAt: sort.sortByUpdatedAt,
          sortBy: undefined,
        },
      }));
    },
    removeSort: () => {
      set((state) => ({
        feFilters: {
          ...state.feFilters,
          sortBy: undefined,
          sortByCreatedAt: undefined,
          sortByUpdatedAt: undefined,
        },
      }));
    },
    setShowDeclinedCreators: (showDeclinedCreators: boolean) => {
      set((state) => ({
        feFilters: { ...state.feFilters, showDeclinedCreators },
        beFilters: { ...state.beFilters, showDeclinedCreators },
      }));
    },
  }))
);
